import { useMemo, useState } from 'react';

import { RecommenderExtendedProfile, Roles } from '@youscience/commonapp-service-common';

import useCommonAppAdminStore from '@store/CommonAppAdminStore';
import useRecommenderStore from '@store/RecommenderStore';

import { PersonalDetailsFields, FormState } from '@constants/commonApp/types';

import {
  CounselorSection,
  PersonalDetailsSection,
  ProfileState,
  Section,
  TeacherSection,
  allSections,
  counselorSections,
  teacherSections,
} from './types';

import { updateCertificationsState, updateSchoolDetailsTeacherState } from './saveUtils';
import useRecommenderProfileStore from '@store/RecommenderProfileStore';
import useNotificationStore from '@store/NotificationStore';

interface SectionState {
  formState: ProfileState;
  expandedSection: Section | null;
  savedSections: Record<Section, boolean>;
}
export const useSaveProfile = () => {
  const { userRoles, updateRecommenderInfo } = useCommonAppAdminStore((store) => store);
  const { recommenderProfile, saveRecommenderProfile } = useRecommenderStore((store) => store);

  const {
    formState: {
      personalDetails: personalDetailsFormState,
      certifications: certificationsFormState,
      schoolDetailsTeacher: schoolDetailsTeacherFormState,
    },
  } = useRecommenderProfileStore((store) => store);

  const isCounselorRole = userRoles?.includes(Roles.COUNSELOR);
  const isTeacherRole = userRoles?.includes(Roles.TEACHER);

  const profileState: ProfileState = {
    [PersonalDetailsSection.PersonalDetails]: null,
    [CounselorSection.Certifications]: null,
    [TeacherSection.SchoolDetailsTeacher]: null,
  };

  const initialSavedSections = Object.values(allSections).reduce(
    (acc, section) => {
      acc[section as Section] = false;
      return acc;
    },
    {} as Record<Section, boolean>,
  );

  const [sectionState, setSectionState] = useState<SectionState>({
    formState: profileState,
    expandedSection: null,
    savedSections: initialSavedSections,
  });

  const allFormsAreValid = useMemo(() => {
    if (isCounselorRole) {
      return personalDetailsFormState.isFormValid && certificationsFormState.isFormValid;
    } else {
      return personalDetailsFormState.isFormValid && schoolDetailsTeacherFormState.isFormValid;
    }
  }, [isCounselorRole, personalDetailsFormState, certificationsFormState, schoolDetailsTeacherFormState]);

  const hasChanges = useMemo(() => {
    const personalDetailsChanged = personalDetailsFormState.hasChanged;
    const counselorChanged = isCounselorRole && certificationsFormState.hasChanged;
    const teacherChanged = isTeacherRole && schoolDetailsTeacherFormState.hasChanged;

    return personalDetailsChanged || counselorChanged || teacherChanged;
  }, [
    isCounselorRole,
    isTeacherRole,
    personalDetailsFormState.hasChanged,
    certificationsFormState.hasChanged,
    schoolDetailsTeacherFormState.hasChanged,
  ]);

  const savePersonalDetails = async (personalDetailsFormState: FormState<PersonalDetailsFields>) => {
    if (personalDetailsFormState.hasChanged && personalDetailsFormState.isFormValid) {
      const {
        data: { fullName, displayName, email, phone },
      } = personalDetailsFormState;

      await updateRecommenderInfo({
        fullName,
        displayName,
        email,
        phone,
      });

      personalDetailsFormState.hasChanged = false;
      personalDetailsFormState.isFormValid = true;
    }
  };

  const saveTeacher = async () => {
    const teacherSectionsToSave = [];
    let teacherData = { ...recommenderProfile.teacherProfile };

    const updateSection = (options: Record<string, unknown>): typeof teacherData => {
      return { ...teacherData, ...options };
    };

    if (schoolDetailsTeacherFormState.hasChanged && schoolDetailsTeacherFormState.isFormValid) {
      teacherSectionsToSave.push(TeacherSection.SchoolDetailsTeacher);
      teacherData = updateSection(updateSchoolDetailsTeacherState(schoolDetailsTeacherFormState));
    }

    return {
      teacherSectionsToSave,
      updates: teacherData,
    };
  };

  const updateCounselorState = async (recommenderProfile: RecommenderExtendedProfile) => {
    const counselorSectionsToSave = [];
    let counselorData = { ...recommenderProfile.counselorProfile };

    const updateSection = (options: Record<string, unknown>): typeof counselorData => {
      return { ...counselorData, ...options };
    };

    if (certificationsFormState.hasChanged && certificationsFormState.isFormValid) {
      counselorSectionsToSave.push(CounselorSection.Certifications);
      counselorData = updateSection(updateCertificationsState(certificationsFormState));
    }

    return {
      counselorSectionsToSave,
      updates: counselorData,
    };
  };

  const saveProfile = async () => {
    let hasChanges = false;

    if (personalDetailsFormState.hasChanged) {
      await savePersonalDetails(personalDetailsFormState);
      hasChanges = true;

      setSectionState((prevState) => ({
        ...prevState,
        savedSections: {
          ...prevState.savedSections,
          [PersonalDetailsSection.PersonalDetails]: true,
        },
      }));
    }

    let counselorData = {};
    let teacherData = {};
    const sectionsToSave: Section[] = [];

    if (isCounselorRole) {
      const { counselorSectionsToSave, updates: counselorUpdates } = await updateCounselorState(recommenderProfile);

      sectionsToSave.push(...counselorSectionsToSave);
      counselorData = counselorUpdates;
    }

    if (isTeacherRole) {
      const { teacherSectionsToSave, updates: teacherUpdates } = await saveTeacher();

      sectionsToSave.push(...teacherSectionsToSave);
      teacherData = teacherUpdates;
    }

    if (sectionsToSave.length > 0) {
      hasChanges = true;
      let data = {};

      if (Object.keys(counselorData).length > 0) {
        data = { ...data, counselorProfile: counselorData };
      }

      if (Object.keys(teacherData).length > 0) {
        data = { ...data, teacherProfile: teacherData };
      }

      if (Object.keys(data).length > 0) {
        void saveRecommenderProfile(data, 'Recommender Profile');
      }

      sectionsToSave.forEach((section) => {
        const state = sectionState.formState[section];
        if (state) {
          state.hasChanged = false;
          state.isFormValid = true;
        }

        setSectionState((prevState) => ({
          ...prevState,
          savedSections: {
            ...prevState.savedSections,
            [section]: true,
          },
        }));
      });
    }

    if (hasChanges) {
      useNotificationStore.getState().notify({
        message: 'Recommender Profile updates have been saved.',
        severity: 'success',
      });
    }
  };

  const saveAndContinue = async (currentSection: Section) => {
    await saveProfile();

    const sections = [
      PersonalDetailsSection.PersonalDetails,
      ...(isCounselorRole ? counselorSections : teacherSections),
    ];

    const currentSectionIndex = sections.indexOf(currentSection);

    let expandedSection: SectionState['expandedSection'] = null;
    if (currentSectionIndex < sections.length - 1) {
      expandedSection = sections[currentSectionIndex + 1];
    }

    setSectionState((prevState) => ({
      ...prevState,
      expandedSection,
    }));
  };

  const toggleExpandedSelection = (section: Section) => {
    let expandedSection: SectionState['expandedSection'] = null;

    if (sectionState.expandedSection !== section) {
      expandedSection = section;
    } else {
      expandedSection = null;
    }

    setSectionState((prevState) => ({
      ...prevState,
      expandedSection,
    }));
  };

  return {
    sectionState,
    saveProfile,
    saveAndContinue,
    toggleExpandedSelection,
    allFormsAreValid,
    hasChanges,
  };
};
